import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  ArrowLeft, Camera, MapPin, Star, Loader2, CheckCircle, Upload, AlertCircle
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SCORING_CATEGORIES = {
  surfaces: { label: "Surfaces", description: "Countertops, tables, shelves" },
  floors: { label: "Floors", description: "All flooring types" },
  appliances: { label: "Appliances", description: "Oven, fridge, microwave" },
  bathroom_buildup: { label: "Bathroom Buildup", description: "Soap scum, hard water, mildew" },
  dust_level: { label: "Dust Level", description: "Visible dust accumulation" },
  kitchen_grease: { label: "Kitchen Grease", description: "Stovetop, hood, backsplash" }
};

const RatingSelector = ({ category, config, value, onChange }) => {
  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <div>
          <p className="font-medium text-zinc-900">{config.label}</p>
          <p className="text-xs text-zinc-500">{config.description}</p>
        </div>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((rating) => (
            <button
              key={rating}
              type="button"
              onClick={() => onChange(category, rating)}
              className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                value >= rating
                  ? 'bg-[#F56F05] text-white'
                  : 'bg-zinc-100 text-zinc-400 hover:bg-zinc-200'
              }`}
              data-testid={`rating-${category}-${rating}`}
            >
              {rating}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const ConditionAssessmentPage = () => {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [appointment, setAppointment] = useState(null);
  const [requiresAssessment, setRequiresAssessment] = useState(null);
  const [existingAssessment, setExistingAssessment] = useState(null);
  
  const [ratings, setRatings] = useState({
    surfaces: 3,
    floors: 3,
    appliances: 3,
    bathroom_buildup: 3,
    dust_level: 3,
    kitchen_grease: 3
  });
  
  const [form, setForm] = useState({
    before_overview_photo_url: '',
    before_highlight_photo_url: '',
    performed_by: user?.role === 'admin' ? 'owner' : 'cleaner',
    performed_by_name: user?.full_name || '',
    notes: ''
  });
  
  const [gpsLocation, setGpsLocation] = useState(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async () => {
    try {
      const [apptRes, requiresRes] = await Promise.all([
        axios.get(`${API_URL}/api/appointments/${appointmentId}`, { headers }),
        axios.get(`${API_URL}/api/appointments/${appointmentId}/requires-assessment`, { headers })
      ]);
      
      setAppointment(apptRes.data);
      setRequiresAssessment(requiresRes.data);
      
      if (requiresRes.data.assessment_exists) {
        const assessRes = await axios.get(`${API_URL}/api/appointments/${appointmentId}/condition-assessment`, { headers });
        setExistingAssessment(assessRes.data);
      }
    } catch (err) {
      toast.error('Failed to load appointment');
    } finally {
      setLoading(false);
    }
  }, [appointmentId, headers]);

  useEffect(() => {
    fetchData();
    getGPSLocation();
  }, [fetchData]);

  const getGPSLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGpsLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => { /* GPS unavailable */ }
      );
    }
  };

  const handleRatingChange = (category, value) => {
    setRatings(prev => ({ ...prev, [category]: value }));
  };

  const calculateScore = () => {
    const weights = { surfaces: 0.18, floors: 0.20, appliances: 0.15, bathroom_buildup: 0.17, dust_level: 0.15, kitchen_grease: 0.15 };
    let total = 0;
    for (const [cat, weight] of Object.entries(weights)) {
      const normalized = (ratings[cat] - 1) * 25;
      total += normalized * weight;
    }
    return Math.round(total);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.before_overview_photo_url || !form.before_highlight_photo_url) {
      toast.error('Both photos are required');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/appointments/${appointmentId}/condition-assessment`, {
        appointment_id: appointmentId,
        ratings,
        before_overview_photo_url: form.before_overview_photo_url,
        before_highlight_photo_url: form.before_highlight_photo_url,
        performed_by: form.performed_by,
        performed_by_name: form.performed_by_name,
        notes: form.notes,
        latitude: gpsLocation?.latitude,
        longitude: gpsLocation?.longitude
      }, { headers });
      
      toast.success('Assessment submitted successfully');
      navigate(`/admin/appointments/${appointmentId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit assessment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#F56F05]" />
      </div>
    );
  }

  const previewScore = calculateScore();

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-zinc-900">Before Condition Assessment</h1>
            <p className="text-sm text-zinc-500">Document property condition before cleaning</p>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6">
        {existingAssessment ? (
          <Card>
            <CardContent className="py-12 text-center">
              <CheckCircle className="h-16 w-16 mx-auto text-green-500 mb-4" />
              <h2 className="text-2xl font-bold text-zinc-900 mb-2">Assessment Already Completed</h2>
              <p className="text-zinc-600 mb-4">
                Before Score: <span className="font-bold text-[#F56F05]">{existingAssessment.before_score}</span>
              </p>
              <p className="text-sm text-zinc-500">
                Performed by {existingAssessment.performed_by_name} on {new Date(existingAssessment.created_at).toLocaleDateString()}
              </p>
            </CardContent>
          </Card>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Live Score Preview */}
            <Card className="bg-gradient-to-br from-zinc-900 to-zinc-800 text-white">
              <CardContent className="py-8 text-center">
                <p className="text-sm text-zinc-400 mb-2">ESTIMATED BEFORE SCORE</p>
                <p className="text-6xl font-bold text-[#F56F05]" data-testid="preview-score">{previewScore}</p>
                <p className="text-sm text-zinc-400 mt-2">
                  {previewScore <= 40 ? 'Restoration Recommended' :
                   previewScore <= 65 ? 'Improvement In Progress' :
                   previewScore <= 85 ? 'Good Condition' : 'Excellent Condition'}
                </p>
              </CardContent>
            </Card>

            {/* Category Ratings */}
            <Card>
              <CardHeader>
                <CardTitle>Condition Ratings</CardTitle>
                <CardDescription>Rate each category from 1 (Poor) to 5 (Excellent)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {Object.entries(SCORING_CATEGORIES).map(([category, config]) => (
                  <RatingSelector
                    key={category}
                    category={category}
                    config={config}
                    value={ratings[category]}
                    onChange={handleRatingChange}
                  />
                ))}
              </CardContent>
            </Card>

            {/* Photos */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Camera className="h-5 w-5" /> Required Photos
                </CardTitle>
                <CardDescription>Capture before condition with GPS timestamp</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Before Overview Photo *</Label>
                  <Input
                    value={form.before_overview_photo_url}
                    onChange={(e) => setForm(prev => ({ ...prev, before_overview_photo_url: e.target.value }))}
                    placeholder="https://... (paste photo URL)"
                    data-testid="overview-photo-url"
                  />
                  <p className="text-xs text-zinc-500 mt-1">Wide shot showing overall room condition</p>
                </div>
                <div>
                  <Label>Before Highlight Photo *</Label>
                  <Input
                    value={form.before_highlight_photo_url}
                    onChange={(e) => setForm(prev => ({ ...prev, before_highlight_photo_url: e.target.value }))}
                    placeholder="https://... (paste photo URL)"
                    data-testid="highlight-photo-url"
                  />
                  <p className="text-xs text-zinc-500 mt-1">Close-up of area needing most attention</p>
                </div>
                
                {gpsLocation && (
                  <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg">
                    <MapPin className="h-4 w-4" />
                    GPS Location Captured: {gpsLocation.latitude.toFixed(4)}, {gpsLocation.longitude.toFixed(4)}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Assessment Details */}
            <Card>
              <CardHeader>
                <CardTitle>Assessment Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Performed By *</Label>
                    <Select
                      value={form.performed_by}
                      onValueChange={(v) => setForm(prev => ({ ...prev, performed_by: v }))}
                    >
                      <SelectTrigger data-testid="performed-by-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="owner">Owner</SelectItem>
                        <SelectItem value="partner">Partner</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="cleaner">Cleaner</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Your Name *</Label>
                    <Input
                      value={form.performed_by_name}
                      onChange={(e) => setForm(prev => ({ ...prev, performed_by_name: e.target.value }))}
                      placeholder="Full name"
                      data-testid="performer-name"
                    />
                  </div>
                </div>
                <div>
                  <Label>Notes (Optional)</Label>
                  <Textarea
                    value={form.notes}
                    onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Any observations or concerns..."
                    rows={3}
                    data-testid="assessment-notes"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Submit */}
            <Button
              type="submit"
              size="lg"
              disabled={submitting}
              className="w-full bg-[#F56F05] hover:bg-[#C2410C]"
              data-testid="submit-assessment-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Submit Before Assessment
                </>
              )}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ConditionAssessmentPage;
